import { publishRealtimeFolderHlsApi, validateDataCompleteness } from "../../../../../api/fastApi";
import { getRuSyncStatusApi, retryRuSyncApi } from "../../../../../api/fastApi";
import { createPortal } from "react-dom";
import { useSettingsSlot } from "utils/settingsSlots";
import { SETTINGS_ACTIONS } from "../Footer/settingsActions";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { CreateSubtitleByInterval } from "../Footer/components/CreateSubtitleByInterval";
import { ManualOnlyBadge } from "../ManualOnly";
import { AutoEditGate, autoEditBlockedReason } from "../ManualOnly/AutoEditGate";

import { useNavigate, useLocation } from "react-router-dom";
import { removeChange, undoCaptionsTranslate } from "utils";
import { PlayButton } from "components/PlayButton";
import { FolderList } from "components/FolderList";
import { SaveButton } from "components/SaveButton";
import { Button, Segmented, Modal, Table, Tooltip, Select, Tabs, InputNumber, Radio, notification, Input } from "antd";
import { checkEmptySpots, approveEmptySpot, unapproveEmptySpot } from "../../../../../api/fastApi";
import { LocalStorageKeys } from "../../../../../providers/AudioProvider/types";
import { useAuth } from "../../../../../providers/AuthProvider";
import { useUiAudioStateContext } from "../../../../../hooks/useUiAudioStateContext";
import { useIsEnglishUi } from "utils/uiLanguage";
import { StreamProgress } from "../StreamProgress";

function getStageStyle(stage) {
    if (stage.total === 0) {
        return { color: "#8c8c8c", backgroundColor: "#f5f5f5", borderColor: "#d9d9d9" };
    }
    if (stage.done === stage.total) {
        return { color: "#52c41a", backgroundColor: "#f6ffed", borderColor: "#b7eb8f" };
    }
    if (stage.done > 0 || stage.inProgress > 0) {
        return { color: "#fa8c16", backgroundColor: "#fff7e6", borderColor: "#ffd591" };
    }
    return { color: "#8c8c8c", backgroundColor: "#f5f5f5", borderColor: "#d9d9d9" };
}

function getStageText(stage) {
    if (stage.total === 0) return `${stage.label}`;
    if (stage.done === stage.total) return `${stage.label} готово ${stage.done}`;
    if (stage.inProgress > 0) return `${stage.label} в процессе ${stage.done}/${stage.total}`;
    if (stage.done > 0) return `${stage.label} готово ${stage.done}/${stage.total}`;
    return `${stage.label}`;
}

const languages = {
    english: {
        title: "En",
        titleLong: "английский",
    },
    de: {
        title: "De",
        titleLong: "немецкий",
    },
    es: {
        title: "Es",
        titleLong: "испанский",
    },
    chinese: {
        title: "汉",
        titleLong: "китайский",
    },
};

const langUrlOptions = [
    { value: "english", label: "English" },
    { value: "de", label: "Deutsch" },
    { value: "es", label: "Español" },
    { value: "zh", label: "中文" },
    { value: "tr", label: "Türkçe" },
    { value: "ar", label: "العربية" },
];

export function Head2({
    playShown = true,
    playSpacebar = true,
    titleShown = true,
    foldersShown = true,
    languageShown = true,
    originalLanguageShown = false,
    saveShown = "changed",
    videoToggleShown = true,
    context,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const uiState = useUiAudioStateContext();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const searchInputRef = useRef(null);

    // Check if translation is from English (or other non-Russian source if implied)
    const searchParams = new URLSearchParams(location.search);
    const [validationData, setValidationData] = useState(null);
    const [activeTab, setActiveTab] = useState("1");
    const [emptySpotsData, setEmptySpotsData] = useState([]);
    const [emptySpotsLoading, setEmptySpotsLoading] = useState(false);
    const [minDurationSec, setMinDurationSec] = useState(() => {
        return parseFloat(localStorage.getItem("minDurationSec") || "5");
    });
    const [folderRange, setFolderRange] = useState("current"); // "current" | "all"
    const [applyingRu, setApplyingRu] = useState(false);
    const [hideApprovedSpots, setHideApprovedSpots] = useState(true);
    const [publishBusy, setPublishBusy] = useState(false);
    const [ruSync, setRuSync] = useState(null);

    const realtimeStatus = context.state.realtimeEditorStatus;
    const isRealtimeStream = Boolean(realtimeStatus?.is_realtime_stream);
    // Редактор эфира: шапка в одну строку постоянной высоты (см. x_head_one_line в main.css). По пути, а не по
    // ответу статуса эфира: тот приходит позже, и переключение режима само сдвинуло бы ленту.
    const oneLineHead = location.pathname.startsWith("/fast/");
    const enUi = useIsEnglishUi();
    // «Во все языки» заново переводит и переозвучивает папку по правкам оригинала — автоправка;
    // в режиме «Без автоправок» выключена (см. ManualOnly/AutoEditGate).
    const autoEdit = { blocked: Boolean(context.state.manualOnly), reason: autoEditBlockedReason(enUi) };

    // Each of these actions is shown in the settings panel, in the group it belongs to, and
    // renders into the row the panel builds for it. «Update HLS» exists only for lectures
    // recorded as a stream, and says so, so its row is absent everywhere else instead of empty.
    // See utils/settingsSlots.
    const checkDataSlot = useSettingsSlot(SETTINGS_ACTIONS.checkData);
    const updateHlsSlot = useSettingsSlot(SETTINGS_ACTIONS.updateHls, isRealtimeStream);
    const managementBoardSlot = useSettingsSlot(SETTINGS_ACTIONS.managementBoard);
    const allLanguagesSlot = useSettingsSlot(SETTINGS_ACTIONS.allLanguages);
    const realtimeLanguage = context.state.lang === "ch" ? "zh" : context.state.lang;
    const currentRealtimeFolder = realtimeStatus?.folders?.find(
        (item) => item.folder_num === Number(context.state.folder),
    );
    const currentPublishJob = currentRealtimeFolder?.languages?.[realtimeLanguage]?.publish_job;
    const publishInProgress = publishBusy || ["queued", "running"].includes(currentPublishJob?.status);
    const publishStage = currentPublishJob?.stage || currentPublishJob?.status;

    useEffect(() => {
        if (!isRealtimeStream || !context.state.mediaId || !context.state.folder) {
            setRuSync(null);
            return undefined;
        }
        let mounted = true;
        const refresh = async () => {
            try {
                const status = await getRuSyncStatusApi(context.state.mediaId, Number(context.state.folder));
                if (mounted) setRuSync(status);
            } catch (_) {
                // Status is optional and must never interfere with editor work.
            }
        };
        void refresh();
        const timer = window.setInterval(refresh, 5000);
        return () => {
            mounted = false;
            window.clearInterval(timer);
        };
    }, [isRealtimeStream, context.state.mediaId, context.state.folder]);

    const currentDetailedErrors = (validationData?.validation_errors?.filter(e => e.folder === parseInt(context.state.folder)) || [])
        .sort((a, b) => parseFloat(a.start_time || 0) - parseFloat(b.start_time || 0));
    const visibleEmptySpots = useMemo(() => {
        if (!hideApprovedSpots) {
            return emptySpotsData;
        }
        return emptySpotsData.filter((spot) => !spot.approved);
    }, [emptySpotsData, hideApprovedSpots]);

    const invalidFolders = validationData?.invalid_folders || [];
    const currentFolderErrors = validationData?.folders_details?.find(d => d.folder === parseInt(context.state.folder));

    const shouldFilterFoldersByOriginalTranslations =
        location.pathname.startsWith("/fast/") &&
        context.state.lang !== context.state.originalLang;

    const visibleFolders = useMemo(() => {
        // Realtime status is durable and arrives before the full subtitle payload.
        // Do not hide a newly completed stream folder merely because getAllSubtitles
        // has not fetched it yet; otherwise it appears only after a page reload.
        if (isRealtimeStream && location.pathname.startsWith("/fast/")) {
            return (realtimeStatus?.folders || [])
                .filter((item) => {
                    if (context.state.lang === context.state.originalLang) {
                        return item.source_ready;
                    }
                    return Boolean(item.languages?.[realtimeLanguage]?.subtitles_ready);
                })
                .map((item) => Number(item.folder_num))
                .filter((folderNum) => Number.isInteger(folderNum) && folderNum >= 1)
                .sort((a, b) => a - b);
        }

        if (!shouldFilterFoldersByOriginalTranslations) {
            return undefined;
        }

        const foldersData = context.state.subtitlesDataAll?.[context.state.lang];
        if (!Array.isArray(foldersData)) {
            return [];
        }

        return Array.from(
            new Set(
                foldersData
                    .map((folder) => {
                        const folderNum = Number(folder?.folder_num);
                        const hasOriginalTranslations =
                            Array.isArray(folder?.original_translations) &&
                            folder.original_translations.length > 0;

                        if (!hasOriginalTranslations || !Number.isInteger(folderNum) || folderNum < 1) {
                            return null;
                        }
                        return folderNum;
                    })
                    .filter((folderNum) => folderNum !== null),
            ),
        ).sort((a, b) => a - b);
    }, [
        isRealtimeStream,
        realtimeStatus,
        realtimeLanguage,
        context.state.originalLang,
        shouldFilterFoldersByOriginalTranslations,
        context.state.subtitlesDataAll,
        context.state.lang,
        location.pathname,
    ]);

    const finishEditing = () => {
        if (!context.state.lang || context.state.lang === context.state.originalLang) {
            context.handles.metadataTranslate(context.state.folder, context.state.toLang);
        } else {
            context.handles.audioSynthesize(
                null,
                context.state.folder,
                context.state.mediaId,
                context.state.lang,
            );
        }
    };

    const fixName = (name) => {
        if (name && typeof name === "string") {
            name = name.replaceAll("_", " ");
            return name;
        } else {
            return "Lecture without a name";
        }
    };

    const fixLang = (lang) => {
        if (!lang || !languages[lang]) return;
        return `Рус → ${languages[lang]["title"]}`;
    };

    const handleVideo = () => {
        const currentUrl = new URL(window.location.href);
        const searchParams = new URLSearchParams(currentUrl.search);

        if (context.state.video) {
            // Remove the 'video' parameter if it exists
            searchParams.delete("video");
            context.handles.setVideo();
        } else {
            // Add the 'video' parameter
            searchParams.set("video", "1");
            context.handles.setVideo("sm");
        }

        // Construct the new path
        const newPath = `${location.pathname}?${searchParams.toString()}`;
        navigate(newPath);
    };

    const undoChange = () => {
        const found = context.state[saveShown].findLast(
            (item) => item.action === "text" && item.id.startsWith(`${context.state.folder}_`),
        );
        const foundId = found.id.split("_");
        undoCaptionsTranslate(
            {
                id: found.id,
                folder_num: foundId[0],
                original_id: foundId[1],
            },
            found.prevText,
            context,
        );
        removeChange(found, "changedTranslation", context);
    };

    const currentUrlLang = searchParams.get("lang");

    const handleLangSwitch = (newLang) => {
        const params = new URLSearchParams(location.search);
        params.set("lang", newLang);
        window.location.href = `${location.pathname}?${params.toString()}`;
    };

    const searchMatches = useMemo(() => {
        if (!uiState.searchQuery) return [];
        const q = uiState.searchQuery.toLowerCase();
        const matches = [];
        const originalTracks = context.state.translationTrackAll[context.state.originalLang] || [];
        const translatedTracks = context.state.translationTrackAll[context.state.lang] || [];
        originalTracks.forEach((track, idx) => {
            if (track.captions && track.captions.toLowerCase().includes(q)) {
                matches.push({ trackId: track.id, start: track.start, side: "original", idx });
            }
        });
        translatedTracks.forEach((track, idx) => {
            if (track.translation && track.translation.toLowerCase().includes(q)) {
                matches.push({ trackId: track.id, start: track.start, side: "translated", idx });
            }
        });
        matches.sort((a, b) => a.start - b.start);
        return matches;
    }, [uiState.searchQuery, context.state.translationTrackAll, context.state.originalLang, context.state.lang]);

    const scrollToMatch = useCallback((index) => {
        if (!searchMatches.length) return;
        const match = searchMatches[index];
        if (!match) return;
        const zoomLevel = 100 - (context.state.zoomLevel || 0);
        const yPos = match.start / zoomLevel;
        context.handles.setScrollTo(yPos);
    }, [searchMatches, context.state.zoomLevel, context.handles]);

    const handleSearchChange = useCallback((e) => {
        uiState.setSearchQuery(e.target.value);
        uiState.setSearchMatchIndex(0);
    }, [uiState]);

    const handleSearchNext = useCallback(() => {
        if (!searchMatches.length) return;
        const nextIdx = (uiState.searchMatchIndex + 1) % searchMatches.length;
        uiState.setSearchMatchIndex(nextIdx);
        scrollToMatch(nextIdx);
    }, [searchMatches, uiState.searchMatchIndex, scrollToMatch, uiState]);

    const handleSearchPrev = useCallback(() => {
        if (!searchMatches.length) return;
        const prevIdx = (uiState.searchMatchIndex - 1 + searchMatches.length) % searchMatches.length;
        uiState.setSearchMatchIndex(prevIdx);
        scrollToMatch(prevIdx);
    }, [searchMatches, uiState.searchMatchIndex, scrollToMatch, uiState]);

    const handleSearchKeyDown = useCallback((e) => {
        if (e.key === "Enter") {
            if (e.shiftKey) {
                handleSearchPrev();
            } else {
                handleSearchNext();
            }
        }
        if (e.key === "Escape") {
            uiState.setSearchQuery("");
            setSearchOpen(false);
        }
    }, [handleSearchNext, handleSearchPrev, uiState]);

    useEffect(() => {
        if (searchOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [searchOpen]);

    // Ctrl+F to open search
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "f") {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    const managementBoardUrl = `https://translation-manag.dev.torsunov.pro/project/${context.state.mediaId}`;

    const handlePlaySavedTrack = () => {
        if (
            !context.state.playing &&
            context.state.changedAudioTrackIds &&
            context.state.changedAudioTrackIds.length > 0
        ) {
            context.handles.setPlaySavedTrack(true);
        }
    };

    const handlePlayPaused = () => {
        const pausedTime = localStorage.getItem(LocalStorageKeys.pausedTime);
        if (pausedTime && !context.state.playing) {
            context.handles.setPlaying(true);
            context.handles.setStartPlaying(true);
            context.handles.setPlayingFragment(null);
            context.handles.setPlayOriginalStart(+pausedTime);
            context.handles.setPlaybackTime(+pausedTime);
        }
    };

    return (
        <>
            <div className="x_head0">
                <div className="x_head_border" />

                {titleShown && (
                    <div className="x_head_title_row">
                        {(() => {
                            const mediaId = context.state.mediaId || "";
                            const rawName = context.state["name"] || context.state.lectureName;
                            // A real, human name only when it differs from the media id
                            // (lectures without a set name store the id as the name).
                            const hasRealName =
                                rawName && typeof rawName === "string" && rawName !== mediaId;
                            return (
                                <>
                                    <span>{hasRealName ? fixName(rawName) : (mediaId || "Lecture without a name")}</span>
                                    {hasRealName && mediaId && (
                                        <span
                                            style={{
                                                marginLeft: 10,
                                                fontWeight: 400,
                                                fontSize: "0.7em",
                                                color: "#999",
                                                userSelect: "all",
                                            }}
                                        >
                                            {mediaId}
                                        </span>
                                    )}
                                </>
                            );
                        })()}
                        {/* Режим пары «без автоправок» меняет, что сохраняет каждая правка, —
                            работать в нём, не видя этого, нельзя. */}
                        <ManualOnlyBadge
                            manualOnly={Boolean(context.state.manualOnly)}
                            state={context.state.manualOnlyState}
                        />
                        {isAdmin && context.state.lectureStatus?.length > 0 && (
                            <span style={{ marginLeft: "10px", display: "inline-flex", gap: "4px", flexWrap: "wrap" }}>
                                {context.state.lectureStatus.map((stage) => {
                                    const style = getStageStyle(stage);
                                    return (
                                        <span
                                            key={stage.key}
                                            style={{
                                                padding: "2px 8px",
                                                fontSize: "11px",
                                                borderRadius: "4px",
                                                border: `1px solid ${style.borderColor}`,
                                                backgroundColor: style.backgroundColor,
                                                color: style.color,
                                                fontWeight: 500,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {getStageText(stage)}
                                        </span>
                                    );
                                })}
                            </span>
                        )}
                    </div>
                )}

                {searchOpen && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "4px 10px",
                        backgroundColor: "#f5f5f5",
                        borderBottom: "1px solid #e0e0e0",
                    }}>
                        <Input
                            ref={searchInputRef}
                            placeholder="Поиск по словам..."
                            value={uiState.searchQuery}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                            size="small"
                            style={{ width: 220 }}
                            allowClear
                        />
                        {uiState.searchQuery && (
                            <span style={{ fontSize: 11, color: "#666", whiteSpace: "nowrap" }}>
                                {searchMatches.length > 0
                                    ? `${uiState.searchMatchIndex + 1} / ${searchMatches.length}`
                                    : "0 / 0"}
                            </span>
                        )}
                        <Button size="small" onClick={handleSearchPrev} disabled={!searchMatches.length}>↑</Button>
                        <Button size="small" onClick={handleSearchNext} disabled={!searchMatches.length}>↓</Button>
                        <Button
                            size="small"
                            type="text"
                            onClick={() => {
                                uiState.setSearchQuery("");
                                setSearchOpen(false);
                            }}
                        >
                            ✕
                        </Button>
                    </div>
                )}

                {/* Редактор эфира (/fast): строка кнопок — одна строка постоянной высоты. Счётчики и статусы
                    («правок в работе», «зрители видят с…», ход своей правки) появляются и пропадают, и раньше
                    строка от этого переносилась: поле «#»/Go уходило на вторую строку, шапка росла на 20 px, и
                    лента под ней съезжала (проба P50m10, E6). Теперь блок статусов сжимается и обрезается внутри
                    себя (полный текст — в подсказке и по клику), а высота шапки от них не зависит. */}
                <div className={`x_head_controls_row${oneLineHead ? " x_head_one_line" : ""}`}>
                <div className="x_head_l">
                    <div className="x_head_logo">
                        {playShown && (
                            <div className="x_player_control">
                                <PlayButton
                                    playSpacebar={playSpacebar}
                                    enter={context.state.pop ? false : true}
                                    context={context}
                                />
                            </div>
                        )}

                        {foldersShown && (
                            <div className="x_head_chapters0">
                                <FolderList
                                    foldersTotal={context.state.foldersTotal}
                                    activeFolder={parseInt(context.state.folder)}
                                    invalidFolders={invalidFolders}
                                    visibleFolders={visibleFolders}
                                    oneLine={oneLineHead}
                                />
                            </div>
                        )}

                        {languageShown && (
                            <div className="x_head_logo_lang">{fixLang(context.state["lang"])}</div>
                        )}

                        {currentUrlLang && isAdmin && (
                            <Select
                                value={currentUrlLang}
                                onChange={handleLangSwitch}
                                size="small"
                                style={{ width: 110, marginLeft: 5 }}
                                options={langUrlOptions}
                            />
                        )}

                    </div>
                    <div className="x_head_center">
                        {originalLanguageShown && (
                            <div className="_fm">
                                <Segmented
                                    options={["2 языка", "Только перевод"]}
                                    value={
                                        context.state.showOriginalLanguage
                                            ? "2 языка"
                                            : "Только перевод"
                                    }
                                    onChange={(value) => {
                                        context.handles.setShowOriginalLanguage(
                                            value === "2 языка",
                                        );
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="x_head_r">
                    {isRealtimeStream && location.pathname.startsWith("/fast/") && (
                        <StreamProgress
                            mediaId={context.state.mediaId}
                            folder={Number(context.state.folder)}
                            lang={context.state.lang}
                            isRealtimeStream={isRealtimeStream}
                        />
                    )}
                    {isRealtimeStream && ruSync && ruSync.status !== "idle" && (
                        <span style={{ fontSize: 11, marginRight: 8 }}>
                            RU sync: {ruSync.status}
                            {ruSync.status === "failed" && ruSync.job_id && (
                                <Button
                                    size="small"
                                    style={{ marginLeft: 4 }}
                                    onClick={async () => {
                                        const next = await retryRuSyncApi(ruSync.job_id, context.state.mediaId);
                                        setRuSync(next);
                                    }}
                                >
                                    Retry
                                </Button>
                            )}
                        </span>
                    )}
                    <button
                        onClick={() => setSearchOpen(!searchOpen)}
                        style={{
                            fontSize: 11,
                            cursor: "pointer",
                            border: searchOpen ? "1px solid #1677ff" : "1px solid #ccc",
                            padding: "2px 8px",
                            background: searchOpen ? "#e6f4ff" : "#f0f0f0",
                            borderRadius: "3px",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Поиск
                    </button>
                    {location.pathname.includes("translate") && (
                        <a
                            href={`/subtitles/${context.state["mediaId"]}?folder=${context.state["folder"]}&${context.state["lang"] ? `lang=${context.state["lang"]}` : ""}`}
                        >
                            Субтитры
                        </a>
                    )}
                    {!context.state.lang || context.state.lang === context.state.originalLang ? (
                        <>
                            {/*<button*/}
                            {/*  onClick={() =>*/}
                            {/*    context.handles.metadataTranslate(context.state.folder, context.state.toLang)*/}
                            {/*  }*/}
                            {/*>*/}
                            {/*  Создать перевод (использует платные ресурсы)*/}
                            {/*</button>*/}
                            {location.pathname.includes("subtitles") && (
                                <a
                                    href={`/translate/${context.state["mediaId"]}?folder=${context.state["folder"]}&${context.state["lang"] ? `lang=${context.state["lang"]}` : ""}`}
                                >
                                    Перевод
                                </a>
                            )}
                        </>
                    ) : (
                        <>
                            {location.pathname.includes("subtitles") && (
                                <a
                                    href={`/${context.state["mediaId"]}?folder=${context.state["folder"]}&${context.state["lang"] ? `lang=${context.state["lang"]}` : ""}`}
                                >
                                    Озвучка
                                </a>
                            )}
                        </>
                    )}
                    <div />
                    {saveShown && (
                        <>
                            <button style={{ width: "22px" }} onClick={handlePlaySavedTrack}>
                                U
                            </button>
                            <button style={{ width: "22px" }} onClick={handlePlayPaused}>
                                I
                            </button>
                            <SaveButton context={context} />
                        </>
                    )}
                    {videoToggleShown && (
                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <CreateSubtitleByInterval />
                                <div
                                    className={`x_player_video_set_1 ${context.state.video ? "active" : ""}`}
                                    // onClick={() => context.handles.setVideo(context.state.video ? false : 'sm')}
                                    onClick={() => {
                                        handleVideo();
                                    }}
                                >
                                    <div className="ico">videocam</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                </div>
            </div >
            <Modal
                title={`Check Data: Folder ${context.state.folder}`}
                open={isModalOpen}
                onOk={() => setIsModalOpen(false)}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={800}
            >
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <Tabs.TabPane tab="Errors" key="1">
                        <Table
                            dataSource={currentDetailedErrors}
                            rowKey="id"
                            pagination={{ pageSize: 5 }}
                            columns={[
                                {
                                    title: "Time",
                                    dataIndex: "start_time",
                                    key: "start_time",
                                    width: 80,
                                    render: (val) => {
                                        if (!val) return "";
                                        if (typeof val === "string" && val.includes(":")) return val.split(".")[0];
                                        let num = parseFloat(val);
                                        if (isNaN(num)) return val;
                                        num = num / 1000;
                                        const m = Math.floor(num / 60);
                                        const s = Math.floor(num % 60);
                                        return `${m}:${s.toString().padStart(2, "0")}`;
                                    }
                                },
                                { title: "Text", dataIndex: "source_text", key: "source_text", ellipsis: true },
                                {
                                    title: "Issue", dataIndex: "missing", key: "missing", width: 170, render: (files) => {
                                        const errors = [];
                                        const isJsonMissing = files.some((f) => f.endsWith(".json"));
                                        const isWavMissing = files.some((f) => f.endsWith(".wav"));

                                        if (isJsonMissing) errors.push("Missing translation");
                                        if (isWavMissing) errors.push("Missing audio");

                                        return (
                                            <div style={{ color: "red", fontSize: "12px" }}>
                                                {errors.map((e, i) => <div key={i}>{e}</div>)}
                                            </div>
                                        );
                                    }
                                }
                            ]}
                            size="small"
                            locale={{ emptyText: "No errors found in this folder" }}
                        />
                    </Tabs.TabPane>

                    <Tabs.TabPane tab="Empty Segments" key="2">
                        <div style={{ marginBottom: 15, display: "flex", gap: "15px", alignItems: "center", flexWrap: "wrap" }}>
                            <div>
                                Minimum pause duration (sec):{" "}
                                <InputNumber
                                    min={1}
                                    value={minDurationSec}
                                    onChange={(v) => {
                                        const nextValue = typeof v === "number" ? v : 1;
                                        setMinDurationSec(nextValue);
                                        localStorage.setItem("minDurationSec", nextValue.toString());
                                    }}
                                />
                            </div>
                            <div>
                                <Radio.Group value={folderRange} onChange={(e) => setFolderRange(e.target.value)}>
                                    <Radio.Button value="current">Current folder</Radio.Button>
                                    <Radio.Button value="all">All folders up to current</Radio.Button>
                                </Radio.Group>
                            </div>
                            <Button
                                type="primary"
                                loading={emptySpotsLoading}
                                onClick={async () => {
                                    setEmptySpotsLoading(true);
                                    try {
                                        const lang = context.state.lang || "en";
                                        const languageFrom = context.state.originalLang || "ru";

                                        const res = await checkEmptySpots({
                                            mediaId: context.state.mediaId,
                                            lang,
                                            languageFrom,
                                            folder: folderRange === "current" ? parseInt(context.state.folder) : undefined,
                                            endFolder: folderRange === "all" ? parseInt(context.state.folder) : undefined,
                                            minDurationSec,
                                        });

                                        setEmptySpotsData(res.empty_spots || []);
                                    } catch (e) {
                                        notification.error({ message: "Failed to check pauses", description: e.message });
                                    } finally {
                                        setEmptySpotsLoading(false);
                                    }
                                }}
                            >
                                Check pauses
                            </Button>
                            <Button type={hideApprovedSpots ? "default" : "dashed"} onClick={() => setHideApprovedSpots((prev) => !prev)}>
                                {hideApprovedSpots ? "Hide approved: ON" : "Hide approved: OFF"}
                            </Button>
                        </div>

                        <Table
                            dataSource={visibleEmptySpots}
                            rowKey="id"
                            pagination={{ pageSize: 5 }}
                            rowClassName={(record) => record.approved ? "approved-spot-row" : ""}
                            columns={[
                                { title: "Folder", dataIndex: "folder", key: "folder", width: 80 },
                                {
                                    title: "Start",
                                    dataIndex: "start_time",
                                    key: "start_time",
                                    width: 100,
                                    render: (val) => {
                                        let num = parseFloat(val) / 1000;
                                        const m = Math.floor(num / 60);
                                        const s = Math.floor(num % 60);
                                        const ms = Math.floor((num % 1) * 1000);
                                        return `${m}:${s.toString().padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
                                    }
                                },
                                {
                                    title: "Duration",
                                    dataIndex: "duration",
                                    key: "duration",
                                    width: 120,
                                    render: (val) => `${(val / 1000).toFixed(2)} sec`
                                },
                                {
                                    title: "Action",
                                    key: "action",
                                    render: (_, record) => (
                                        <div style={{ display: "flex", gap: "8px" }}>
                                            <Button
                                                size="small"
                                                onClick={async () => {
                                                    const zoomLevel = 100 - (context.state.zoomLevel || 0);
                                                    const yPos = record.start_time / zoomLevel;

                                                    if (parseInt(context.state.folder) === record.folder) {
                                                        context.handles.setScrollTo(yPos);
                                                    } else {
                                                        const scrollStorageKey = `scrollPos_${context.state.mediaId}_${record.folder}`;
                                                        sessionStorage.setItem(scrollStorageKey, String(yPos));

                                                        const url = new URL(window.location.href);
                                                        url.searchParams.set("folder", record.folder);
                                                        navigate(url.pathname + url.search);
                                                    }
                                                    setIsModalOpen(false);
                                                }}
                                            >
                                                Go to
                                            </Button>
                                            <Button
                                                size="small"
                                                type={record.approved ? "dashed" : "default"}
                                                style={{
                                                    backgroundColor: record.approved ? "#f6ffed" : undefined,
                                                    borderColor: record.approved ? "#b7eb8f" : undefined,
                                                }}
                                                onClick={async () => {
                                                    try {
                                                        const lang = context.state.lang || "en";
                                                        if (record.approved) {
                                                            await unapproveEmptySpot({
                                                                mediaId: context.state.mediaId,
                                                                lang,
                                                                folder: record.folder,
                                                                startTime: record.start_time,
                                                                duration: record.duration,
                                                            });
                                                        } else {
                                                            await approveEmptySpot({
                                                                mediaId: context.state.mediaId,
                                                                lang,
                                                                folder: record.folder,
                                                                startTime: record.start_time,
                                                                duration: record.duration,
                                                            });
                                                        }

                                                        setEmptySpotsData((prev) =>
                                                            prev.map((spot) =>
                                                                spot.id === record.id ? { ...spot, approved: !record.approved } : spot
                                                            )
                                                        );
                                                    } catch (e) {
                                                        notification.error({
                                                            message: record.approved ? "Failed to remove approval" : "Failed to approve spot",
                                                            description: e.message,
                                                        });
                                                    }
                                                }}
                                            >
                                                {record.approved ? "Remove approval" : "Approve"}
                                            </Button>
                                        </div>
                                    )
                                }
                            ]}
                            size="small"
                            locale={{
                                emptyText: hideApprovedSpots
                                    ? "No empty pauses to review (approved items are hidden)"
                                    : "No matching empty pauses"
                            }}
                        />
                    </Tabs.TabPane>
                </Tabs>
            </Modal>
                    {checkDataSlot &&
                        createPortal(
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {validationData && (
                                <Tooltip title={`Failed folders: ${validationData.invalid_folders?.join(', ') || 'none'}`}>
                                    <div style={{ fontSize: '10px', color: '#666', cursor: 'help' }}>
                                        Total: {validationData.total_folders} | Valid: {validationData.valid_folders?.length || 0} | Invalid: {validationData.invalid_folders?.length || 0}
                                    </div>
                                </Tooltip>
                            )}
                            <button onClick={async () => {
                                const lang = context.state.lang || 'en';
                                const languageFrom = context.state.originalLang || 'ru';
                                console.log("LOG: Checking data for", context.state.mediaId, lang, "from", languageFrom);
                                
                                setIsModalOpen(true);
                                setActiveTab("1");
                                
                                try {
                                    const d = await validateDataCompleteness({
                                        mediaId: context.state.mediaId,
                                        lang: lang,
                                        languageFrom: languageFrom
                                    });
                                    console.log("LOG: Result", d);
                                    setValidationData(d);
                                } catch (e) {
                                    console.error("LOG: Error", e);
                                    alert("Error: " + e.message);
                                }
                            }} style={{ fontSize: 10, cursor: 'pointer', border: '1px solid #ccc', padding: '2px 5px', background: '#f0f0f0', borderRadius: '3px' }}>
                                {enUi ? "Check" : "Проверить"}
                            </button>
                        </div>,
                            checkDataSlot,
                        )}
                    {updateHlsSlot &&
                        isRealtimeStream &&
                        createPortal(
                        <Button
                            size="small"
                            type="primary"
                            loading={publishInProgress}
                            disabled={publishInProgress}
                            onClick={async () => {
                                try {
                                    setPublishBusy(true);
                                    await context.handles.flushDirtyChanges();
                                    const result = await publishRealtimeFolderHlsApi({
                                        mediaId: context.state.mediaId,
                                        folderNum: Number(context.state.folder),
                                        lang: realtimeLanguage,
                                        languageFrom: context.state.originalLang || "ru",
                                    });
                                    notification.success({
                                        message: "HLS publication queued",
                                        description: `Folder ${context.state.folder}, ${context.state.lang}${result.stage ? `: ${result.stage}` : ""}`,
                                    });
                                    await context.handles.refreshRealtimeEditorStatus();
                                } catch (error) {
                                    notification.error({
                                        message: "Could not publish HLS",
                                        description: error?.message || String(error),
                                    });
                                } finally {
                                    setPublishBusy(false);
                                }
                            }}
                        >
                            {publishInProgress
                                ? `${enUi ? "Updating" : "Обновляем"}${publishStage ? `: ${publishStage}` : "…"}`
                                : enUi ? "Update" : "Обновить"}
                        </Button>,
                            updateHlsSlot,
                        )}
                    {managementBoardSlot &&
                        createPortal(
                        <a
                        href={managementBoardUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            fontSize: 11,
                            cursor: "pointer",
                            border: "1px solid #ccc",
                            padding: "2px 8px",
                            background: "#f0f0f0",
                            borderRadius: "3px",
                            textDecoration: "none",
                            color: "#333",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {enUi ? "Management board" : "Доска управления"}
                    </a>,
                            managementBoardSlot,
                        )}
                    {allLanguagesSlot &&
                        createPortal(
                        <AutoEditGate {...autoEdit}>
                        {/* Выключенной кнопке своя подсказка не нужна — причину говорит обёртка. */}
                        <Tooltip title={autoEdit.blocked ? undefined : "Перевести исправленный русский во все языковые пары (текущая папка): пересоздать перевод и озвучку. Дальнейшие шаги (склейка, HLS, публикация) — вручную по каждому языку."}>
                        <Button
                            size="small"
                            loading={applyingRu}
                            onClick={async () => {
                                if (autoEdit.blocked) return;
                                try {
                                    setApplyingRu(true);
                                    await context.handles.flushDirtyChanges();
                                    const res = await context.handles.applyRuToAllLanguages();
                                    if (!res || res.status === "noop") {
                                        notification.info({
                                            message: "Нет изменённых русских субтитров в этой папке",
                                        });
                                    } else if (typeof res.status === "string" && res.status.startsWith("skipped")) {
                                        // Сервер отказал и назвал причину (пара ведётся переводом или
                                        // включена «Без автоправок»): это не «отправлено».
                                        notification.warning({
                                            message: "Не отправлено",
                                            description: res.reason || res.status,
                                        });
                                    } else {
                                        notification.success({
                                            message: "Русский отправлен во все языки",
                                            description: `Папка ${context.state.folder}: перевод и озвучка пересоздаются в фоне (${(res.pairs || []).join(", ")})`,
                                        });
                                    }
                                } catch (e) {
                                    notification.error({
                                        message: "Не удалось применить",
                                        description: String(e),
                                    });
                                } finally {
                                    setApplyingRu(false);
                                }
                            }}
                        >
                            {enUi ? "Into every language" : "Во все языки"}
                        </Button>
                    </Tooltip>
                        </AutoEditGate>,
                            allLanguagesSlot,
                        )}
        </>
    );
}

